import { NotificationClientService, NotificationType } from '../../../services/basics/notification-client.service';
import { computedFrom, observable } from 'aurelia-binding';
import { I18N } from 'aurelia-i18n';
import { IEditableEntity } from '../../../models/basics/basics.models';
import { EntityTypes, IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { NotifyPropertyChangedBase, Reflection } from '../../../utilities/reflection';
import { ItemSource, DataGridColumn, SourceType, DataType } from "../../../models/queries/queries.models";
import { ValidationsClientService } from "../../../services/validations/validations-client.service";
import { RowItem } from "../../components/data-grid/data-grid";

export abstract class BaseListManager<TEntity extends IEntityTypeIdentifier,
    TRoot extends IEntityTypeIdentifier> extends NotifyPropertyChangedBase {

    private _canAdd: boolean = true;
    private _rootItem: TRoot;

    @observable
    public items: TEntity[] = [];
    public exclude: number[] = [];

    public shouldAddEditAndRemoveColumns(): boolean {
        return true;
    }

    @computedFrom(Reflection.getPropertyPath<BaseListManager<TEntity, TRoot>>(x => x._canAdd))
    public get canAdd(): boolean {
        return this._canAdd;
    }

    public set canAdd(value: boolean) {
        this._canAdd = value;
    }

    public get rootItem(): TRoot {
        return this._rootItem;
    }

    public set rootItem(value: TRoot) {
        this._rootItem = value;
        //in case this has a child View Model
        this.onRootItemSet();
    }

    public get rootEntityType(): EntityTypes {
        if (this.rootItem) {
            return this.rootItem.entityType;
        }
    }

    public activate(model: any) {
        this.rootItem = typeof (model) == "string" ? this.parse(<string>model) : model;
        this.activateOverride(this.rootItem);
    }

    private parse(obj: string): TRoot {
        return JSON.parse(obj.replace(new RegExp("&quot;", "g"), "\"")
            .replace(new RegExp("&gt;", "g"), ">")
            .replace(new RegExp("&lt;", "g"), "<"));
    }

    constructor(protected notificationService: NotificationClientService,
        protected i18N: I18N) {
        super();
    }
    public add(added: TEntity): void {
        this.createArray();
        this.items.push(added);
    }

    public remove($event, item: TEntity) {
        if ($event) {
            $event.stopPropagation();
        }
        this.createArray();
        const index = this.getItemIndex(item);
        if (index !== -1) {
            this.removeByIndex(index);
        }
    }

    public removeByIndex(index: number) {
        this.createArray();
        this.items.splice(index, 1);
    }

    protected getItemIndex(item: TEntity): number {
        this.createArray();
        return this.items.indexOf(item);
    }

    private createArray() {
        if (!this.items) {
            this.items = [];
        }
    }

    protected onRootItemSet() {

    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected activateOverride(root: TRoot) {

    }
}

export interface IBaseLitManagerEntity extends IEntityTypeIdentifier, IEditableEntity {

}

export abstract class BaseListManagerDataGrid<TEntity extends IBaseLitManagerEntity,
    TRoot extends IEntityTypeIdentifier> extends NotifyPropertyChangedBase {

    private _canAdd: boolean = true;

    item: TRoot;

    public abstract get items(): TEntity[];

    public set canAdd(value: boolean) {
        this._canAdd = value;
    }

    @computedFrom(Reflection.getPropertyPath<BaseListManagerDataGrid<TEntity, TRoot>>(x => x._canAdd))
    public get canAdd(): boolean {
        return this._canAdd;
    }

    getValue(item: TEntity, property: string): any {
        const props: string[] = property.split(".");
        if (props.length === 1) {
            if (!item[property]) {
                item[property] = [];
            }

            return item[property];
        }

        if (!item[props[0]]) {
            item[props[0]] = {};
        }
        return this.getValue(item[props[0]], property.substring(property.indexOf(".") + 1));
    }

    public activate(model: TRoot) {
        this.item = typeof (model) == "string" ? this.parse(<string>model) : model;
        this.activateOverride(this.item);
    }

    private parse(obj: string): TRoot {
        return JSON.parse(obj.replace(new RegExp("&quot;", "g"), "\"")
            .replace(new RegExp("&gt;", "g"), ">")
            .replace(new RegExp("&lt;", "g"), "<"));
    }

    public static getColumns(columnNames: DataGridColumn[], edit: (item, row: RowItem, column) => void, remove: (item: any) => void, shouldAddEditAndRemoveColumns: boolean = true) {
        let columns = columnNames;
        if (shouldAddEditAndRemoveColumns)
            columns = BaseListManagerDataGrid.getDefaultColumns(columns, edit, remove);

        return columns;
    }

    private columns: DataGridColumn[] = null;

    public filterFunc: ((items: TEntity[]) => TEntity[]);

    public forceRefreshColumns() {
        const columns = this.getColumnNames();
        if (this.shouldAddEditAndRemoveColumns())
            this.columns = BaseListManagerDataGrid.getDefaultColumns(columns ? columns : [], this.edit, this.remove);
        else {
            this.columns = columns;
        }
        const editColumn = this.columns.filter(x => x.name == "edit")[0];
        if (editColumn) {
            editColumn.isHidden = (item) => !this.shouldEdit(item);
        }
    }

    @computedFrom('changes["gridItems"]', "item")
    public get gridItems(): ItemSource {
        //items for a grid should be returned if this.item has been set
        if (this.item) {
            if (!this.columns) {
                this.forceRefreshColumns();
            }
            if (this.items) {
                for (const item of this.items) {
                    (<any>item).isEditable = false;
                }
            }

            return { source: this.items, sourceType: SourceType.Collection, columns: this.columns, filterFunc: this.filterFunc, getRowClass: this.getRowClass };
        }
        return { source: [], sourceType: SourceType.Collection, columns: [], filterFunc: this.filterFunc, getRowClass: this.getRowClass }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getRowClass(item: any, rowItem: RowItem): string { return "" }

    public shouldAddEditAndRemoveColumns(): boolean {
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public shouldEdit(item?: TEntity): boolean {
        return true;
    }

    constructor(protected validationsClientService: ValidationsClientService,
        protected notificationService: NotificationClientService,
        protected i18N: I18N) {
        super();
    }

    private static getDefaultColumns(columns: DataGridColumn[], edit: (item, row: RowItem, column) => void, remove: (item: any) => void): DataGridColumn[] {
        columns.push(
            {
                name: 'edit',
                title: "",
                dataType: DataType.Icon,
                value: edit,
                parameter: "edit",
                sortOrder: columns.length,
                columnOrder: columns.length,
                instancePerRow: true
            });
        columns.push({
            name: 'remove',
            title: "",
            dataType: DataType.Icon,
            value: remove,
            parameter: "delete",
            sortOrder: columns.length,
            columnOrder: columns.length
        });
        return columns;
    }

    public remove = (item: TEntity) => {
        const index = this.items.indexOf(item);
        this.items.splice(index, 1);
        this.removeCompleted();
    }
    protected removeCompleted() { }

    public edit = (item: TEntity, row: RowItem, column: DataGridColumn) => {
        if (item.isEditable) {
            item.isEditable = false;
            column.parameter = "edit";
            this.onEditCompleted(row, column);
        } else {
            item.isEditable = true;
            column.parameter = "done";
            this.onEditCompleted(row, column);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onEditCompleted(row: RowItem, column: DataGridColumn) {

    }

    protected unableToValidate() {
        this.notificationService.show(this.i18N.tr('validation-error-text') + "!", this.i18N.tr('error-applying-validation-text') + "!", NotificationType.Error);
    }

    public add(): void {
        const newItem: TEntity = this.getNewItem();
        this.items.push(newItem);
        this.onitemAdded(newItem);
    }

    onNewItemAdded = ($event) => {
        setTimeout(() => {
            const rowItem = $event.detail.rowItem as RowItem;
            if (rowItem.item && (!rowItem.item.id || rowItem.item.id <= 0)) {
                this.edit(rowItem.item, rowItem, rowItem.columnKeys["edit"].column);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onitemAdded(item: TEntity): void { }

    public abstract getNewItem(): TEntity;

    public abstract getColumnNames(): DataGridColumn[];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected activateOverride(root: TRoot) {

    }
}
